import { useEffect, useRef } from "react";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import { ReportLevel } from "./adminReportsApiSlice";
import { AdminReportsSubTabs } from "./AdminReports";

interface UseTabChangeResetArgs {
	activeSubTab: string | null;
	maxReportLevel: ReportLevel;
	adminLevelDistrictOptions: SimpleOption[];
	adminLevelSchoolOptions: SimpleOption[];
	courseLevelDistrictOptions: SimpleOption[];
	courseLevelSchoolOptions: SimpleOption[];
	districtFilter: number;
	schoolFilter: number;
	setDistrictFilter: (value: number) => void;
	setSchoolFilter: (value: number) => void;
}

// Reset filters when switching tabs if the selected district/school is not
// available/valid in the newly-active tab (Admin vs Course level options can
// differ, since a district/school admin's max report level restricts the
// Admin-tab options but not the Course-tab options).
export function useTabChangeReset({
	activeSubTab,
	maxReportLevel,
	adminLevelDistrictOptions,
	adminLevelSchoolOptions,
	courseLevelDistrictOptions,
	courseLevelSchoolOptions,
	districtFilter,
	schoolFilter,
	setDistrictFilter,
	setSchoolFilter,
}: UseTabChangeResetArgs) {
	const previousTabRef = useRef<string | null>(null);

	useEffect(() => {
		const previousTab = previousTabRef.current;

		// Initial tab set - just update the ref without validation
		if (previousTab === null) {
			if (activeSubTab !== null) {
				previousTabRef.current = activeSubTab;
			}
			return;
		}

		// Only run when tab actually changes
		if (previousTab === activeSubTab) return;

		// Check if current district is still valid in new tab
		if (districtFilter && districtFilter !== 0) {
			const newDistrictOptions =
				activeSubTab === AdminReportsSubTabs.Admin
					? adminLevelDistrictOptions
					: courseLevelDistrictOptions;

			const isDistrictValid = newDistrictOptions.some(
				(opt) => Number(opt.value) === Number(districtFilter)
			);
			if (!isDistrictValid) {
				if (maxReportLevel === ReportLevel.State) {
					setDistrictFilter(0);
				} else {
					setDistrictFilter(Number(newDistrictOptions[0]?.value) ?? 0);
				}

				previousTabRef.current = activeSubTab;
				return; // District reset will cascade to school/teacher/course
			}
		}

		// Check if current school is still valid in new tab
		if (schoolFilter && schoolFilter !== 0) {
			const newSchoolOptions =
				activeSubTab === AdminReportsSubTabs.Admin
					? adminLevelSchoolOptions
					: courseLevelSchoolOptions;

			const isSchoolValid = newSchoolOptions.some(
				(opt) => Number(opt.value) === Number(schoolFilter)
			);
			if (!isSchoolValid && maxReportLevel !== ReportLevel.School) {
				setSchoolFilter(0);
				previousTabRef.current = activeSubTab;
				return; // School reset will cascade to teacher/course
			}
		}

		previousTabRef.current = activeSubTab;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		activeSubTab,
		adminLevelDistrictOptions,
		adminLevelSchoolOptions,
		courseLevelDistrictOptions,
		courseLevelSchoolOptions,
		districtFilter,
		schoolFilter,
		maxReportLevel,
	]);
}
